import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { TenantConfigProvider } from "./contexts/TenantConfigContext";
import ErrorBoundary from "./components/ErrorBoundary";
import { CookieConsent } from "./components/CookieConsent";
import { Loader2 } from "lucide-react";

// Static imports - 初期表示に必要なコンポーネント
import Auth from "./pages/Auth";
import AppLayout from "./components/layout/AppLayout";
import StudentLayout from "./components/layout/StudentLayout";

// Lazy imports - コード分割対象のページコンポーネント
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Students = lazy(() => import("./pages/Students"));
const Progress = lazy(() => import("./pages/Progress"));
const Analytics = lazy(() => import("./pages/Analytics"));
const StudentAnalytics = lazy(() => import("./pages/StudentAnalytics"));
const Chat = lazy(() => import("./pages/Chat"));
const Videos = lazy(() => import("./pages/Videos"));
const Courses = lazy(() => import("./pages/Courses"));
const Escalations = lazy(() => import("./pages/Escalations"));
const StudentDetail = lazy(() => import("./pages/StudentDetail"));
const AdminCourseDetail = lazy(() => import("./pages/AdminCourseDetail"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Unauthorized = lazy(() => import("./pages/Unauthorized"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Docs = lazy(() => import("./pages/Docs"));
const Settings = lazy(() => import("./pages/Settings"));
const Profile = lazy(() => import("./pages/Profile"));
const LineLinkPage = lazy(() => import("./pages/LineLinkPage"));
const OemWizard = lazy(() => import("./pages/OemWizard"));

// Student pages
const StudentDashboard = lazy(() => import("./pages/student/Dashboard"));
const StudentCourses = lazy(() => import("./pages/student/Courses"));
const StudentProgress = lazy(() => import("./pages/student/Progress"));
const StudentChat = lazy(() => import("./pages/student/Chat"));
const StudentAchievements = lazy(() => import("./pages/student/Achievements"));
const CourseDetail = lazy(() => import("./pages/student/CourseDetail"));
const LessonViewer = lazy(() => import("./pages/student/LessonViewer"));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

const queryClient = new QueryClient();
const basename = (import.meta.env.BASE_URL ?? "/").replace(/\/+$/, "") || "/";

function RoleBasedRedirect() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (!user.role) {
    return <Navigate to="/auth?missingRole=1" replace />;
  }

  if (user.role === 'admin' || user.role === 'educator' || user.role === 'teacher') {
    return <Navigate to="/dashboard" replace />;
  }

  return <Navigate to="/student/dashboard" replace />;
}

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="light" enableSystem storageKey="jarvis-theme">
        <TooltipProvider>
          <AuthProvider>
            <TenantConfigProvider>
              <Toaster />
              <Sonner />
              <CookieConsent />
              <BrowserRouter basename={basename}>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<RoleBasedRedirect />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/unauthorized" element={<Unauthorized />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/docs" element={<Docs />} />

                  {/* LINE Account Link - All authenticated users */}
                  <Route path="/settings/line-link" element={
                    <ProtectedRoute allowedRoles={['admin', 'educator', 'teacher', 'student']}>
                      <LineLinkPage />
                    </ProtectedRoute>
                  } />

                  {/* Admin/Educator/Teacher Routes */}
                  <Route element={
                    <ProtectedRoute allowedRoles={['admin', 'educator', 'teacher']}>
                      <AppLayout />
                    </ProtectedRoute>
                  }>
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/students" element={<Students />} />
                    <Route path="/students/:id" element={<StudentDetail />} />
                    <Route path="/progress" element={<Progress />} />
                    <Route path="/analytics" element={<Analytics />} />
                    <Route path="/analytics/:userId" element={<StudentAnalytics />} />
                    <Route path="/chat" element={<Chat />} />
                    <Route path="/videos" element={<Videos />} />
                    <Route path="/courses" element={<Courses />} />
                    <Route path="/courses/:id" element={<AdminCourseDetail />} />
                    <Route path="/escalations" element={<Escalations />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/profile" element={<Profile />} />
                  </Route>

                  {/* Admin-only Routes (OEM settings) */}
                  <Route element={
                    <ProtectedRoute allowedRoles={['admin', 'owner', 'super_admin']}>
                      <AppLayout />
                    </ProtectedRoute>
                  }>
                    <Route path="/oem-wizard" element={<OemWizard />} />
                  </Route>

                  {/* Student Routes - Admin/Educator/Teacher can also access */}
                  <Route path="/student" element={
                    <ProtectedRoute allowedRoles={['student', 'admin', 'educator', 'teacher']}>
                      <StudentLayout />
                    </ProtectedRoute>
                  }>
                    <Route path="dashboard" element={<StudentDashboard />} />
                    <Route path="courses" element={<StudentCourses />} />
                    <Route path="courses/:courseId" element={<CourseDetail />} />
                    <Route path="progress" element={<StudentProgress />} />
                    <Route path="chat" element={<StudentChat />} />
                    <Route path="achievements" element={<StudentAchievements />} />
                    <Route path="lesson/:lessonId" element={<LessonViewer />} />
                    <Route path="settings" element={<Settings />} />
                  </Route>

                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
            </TenantConfigProvider>
          </AuthProvider>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
